import { PlusOutlined } from '@ant-design/icons';
import { useTranslate } from '@refinedev/core';
import { Button, Tooltip } from 'antd';
import { useState } from 'react';
import {
  AssignHomeworkDrawer,
  type AssignPreset,
} from '../../pages/homework/AssignHomeworkDrawer';
import { useRefreshClientPage } from '../../pages/clients/refreshClientPage';
import { useAssignClient } from './useAssignClient';

/**
 * The ONE Assign button of a tool section (design §1.7) — it opens the existing Assign homework drawer with the tool preselected and
 * its own defaults (`preset`, §1.8). No per-tool assign forms.
 */
export type ToolAssignButtonProps = {
  clientId: string;
  clientName: string;
  preset: AssignPreset;
  /** The section's own refetch (after the page-wide refresh). */
  onAssigned?: () => void;
  /** True inside an EmptyState. */
  primary?: boolean;
  /** Default: "Assign". */
  label?: string;
  /** A disabled button says why. */
  disabledReason?: string;
};

export const ToolAssignButton = ({
  clientId,
  clientName,
  preset,
  onAssigned,
  primary,
  label,
  disabledReason,
}: ToolAssignButtonProps) => {
  const t = useTranslate();
  const [open, setOpen] = useState(false);
  const { target, clientTimeZone } = useAssignClient(
    clientId,
    clientName,
    open,
  );
  const refreshClientPage = useRefreshClientPage();

  const button = (
    <Button
      type={primary ? 'primary' : 'default'}
      icon={<PlusOutlined />}
      disabled={Boolean(disabledReason)}
      onClick={() => setOpen(true)}
      data-testid="tool-section-assign"
    >
      {label ?? t('pages.toolSection.assign')}
    </Button>
  );

  return (
    <>
      {disabledReason ? (
        <Tooltip title={disabledReason}>
          {/* a disabled button swallows hover; the span carries it */}
          <span>{button}</span>
        </Tooltip>
      ) : (
        button
      )}
      <AssignHomeworkDrawer
        open={open}
        clients={[target]}
        clientTimeZone={clientTimeZone}
        preset={preset}
        onClose={() => setOpen(false)}
        onAssigned={() => {
          refreshClientPage('assign');
          onAssigned?.();
        }}
      />
    </>
  );
};
